
import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, ArrowUpDown, MapPin, CalendarX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import EmptyState from "@/components/EmptyState";
import { useFavorites } from "@/components/favorites/FavoritesProvider";
import ExploreMap from "./explore/ExploreMap";
import ExploreResultCard from "./explore/ExploreResultCard";
import ExploreModeToggle, { type ExploreMode } from "./explore/ExploreModeToggle";
import { ExploreHero } from "./explore/ExploreHero";
import { ExplorePaths, deriveActivePathKey } from "./explore/ExplorePaths";
import { ExploreLocationLink } from "./explore/ExploreLocationPrompt";
import { ExploreActiveFilters } from "./explore/ExploreActiveFilters";
import ViewToggle from "./explore/ViewToggle";
import { UtflyktSection } from "./explore/UtflyktSection";
import { ExplorePreviewSheet } from "./explore/ExplorePreviewSheet";
import { ExploreFilterSheet } from "./explore/ExploreFilterSheet";
import { ExploreSortSheet } from "./explore/ExploreSortSheet";
import ExploreRail from "./explore/ExploreRail";
import { useExploreResults, itemKey } from "@/hooks/useExploreResults";
import { useExploreLocation } from "@/hooks/useExploreLocation";
import { useExploreSections } from "@/hooks/useExploreSections";
import { useExploreCardClick } from "@/hooks/useExploreCardClick";
import { useExploreHeroTitle } from "@/hooks/useExploreHeroTitle";
import { getExploreHistory } from "@/lib/exploreHistory";
import { DEFAULT_RADIUS, type RadiusKm } from "@/lib/map/config";
import {
  parseExploreParams,
  buildExploreParamUpdates,
  defaultSortForMode,
  type ExploreParams,
  SORT_LABELS,
} from "@/lib/exploreParams";
import { track } from "@/lib/analytics";
import { derivePreviewText } from "@/lib/text";
import type { ExploreItemWithDistance } from "@/types/explore";
import type { ExploreResultItem } from "@/types/exploreResult";


type ViewMode = "list" | "map";

function parseModeFromUrl(params: URLSearchParams): ExploreMode {
  const raw = params.get("mode");
  if (raw === "do" || raw === "farms") return raw;
  return "places";
}

function mapResultToCardItem(item: ExploreResultItem): ExploreItemWithDistance {
  const shortDescription = derivePreviewText(item.shortDescription, 160);

  return {
    id: item.id,
    kind: "place",
    slug: item.slug,
    name: item.title,
    subtitle: item.subtitle,
    shortDescription,
    tags: item.badges,
    lat: item.lat,
    lng: item.lng,
    coverImageUrl: item.image,
    verified: false,
    href: item.href,
    _distanceKm: item._distanceKm,
    hasFarmShop: item.kind === "activity" ? item.includesFarmSale : item.hasFarmShop,
    hasFarmSaleAlcohol:
      item.kind === "activity" ? item.includesFarmSaleAlcohol : item.hasFarmSaleAlcohol,
  };
}

const ExploreContent = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { listFavorites } = useFavorites();

  const mode = parseModeFromUrl(searchParams);
  const exploreParams = useMemo(() => parseExploreParams(searchParams, mode), [searchParams, mode]);
  const favActive = searchParams.get("fav") === "1";

  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(DEFAULT_RADIUS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

  const exploreLocation = useExploreLocation();
  const nearMeActive = exploreLocation.isActive;
  const userLocation = exploreLocation.userLocation;

  const favIds = useMemo(() => {
    const kind = mode === "farms" ? "producer" : mode === "do" ? "activity" : "place";
    return new Set(listFavorites(kind).map((item) => item.entityId));
  }, [listFavorites, mode]);

  const applyParams = useCallback((updates: Partial<ExploreParams>) => {
    const paramUpdates = buildExploreParamUpdates(updates, mode);
    setSearchParams((prev) => {
      Object.entries(paramUpdates).forEach(([key, value]) => {
        if (value === null) prev.delete(key);
        else prev.set(key, value);
      });
      return prev;
    }, { replace: true });
  }, [mode, setSearchParams]);

  const toggleFav = useCallback(() => {
    setSearchParams((prev) => {
      if (prev.get("fav") === "1") prev.delete("fav");
      else prev.set("fav", "1");
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const clearAllFilters = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("when");
      prev.delete("type");
      prev.delete("sort");
      prev.delete("tags");
      prev.delete("near");
      prev.delete("fav");
      return prev;
    }, { replace: true });
    exploreLocation.clear();
    setSearchQuery("");
  }, [exploreLocation, setSearchParams]);

  useEffect(() => {
    const urlNear = searchParams.get("near") === "1";
    if (exploreLocation.isActive && !urlNear) {
      setSearchParams((prev) => {
        prev.set("near", "1");
        prev.set("sort", "nearest");
        return prev;
      }, { replace: true });
    } else if (!exploreLocation.isActive && urlNear) {
      setSearchParams((prev) => {
        prev.delete("near");
        if (prev.get("sort") === "nearest") prev.delete("sort");
        return prev;
      }, { replace: true });
    }
  }, [exploreLocation.isActive, searchParams, setSearchParams]);

  const { results, mapItems, isLoading } = useExploreResults({
    mode,
    placeFilters: { pageSize: 200, sort: "standard" },
    nearMeActive,
    userLocation,
    radiusKm,
    favActive,
    favIds,
    searchQuery: searchQuery.trim() || undefined,
    exploreParams,
  });

  useEffect(() => {
    if (!selectedKey) return;
    const stillExists = results.some((item) => itemKey(item) === selectedKey);
    if (!stillExists) {
      setSelectedKey(null);
      setPreviewOpen(false);
    }
  }, [results, selectedKey]);

  const selectedItem = useMemo(
    () => results.find((item) => itemKey(item) === selectedKey) ?? null,
    [results, selectedKey],
  );

  const exploreHistory = useMemo(() => getExploreHistory(), []);
  const sections = useExploreSections(results, {
    hasLocation: nearMeActive && !!userLocation,
    curatedTags: exploreHistory.tags,
    curatedIds: exploreHistory.ids,
    mode,
  });

  const heroTitle = useExploreHeroTitle({
    locationState: exploreLocation.location,
    boundsActive: false,
  });

  const handleModeChange = useCallback((nextMode: ExploreMode) => {
    track("filter_toggle", { filter_key: `mode:${nextMode}`, enabled: true }, "explore");
    setSelectedKey(null);
    setPreviewOpen(false);
    setSearchParams((prev) => {
      if (nextMode === "places") prev.delete("mode");
      else prev.set("mode", nextMode);
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  const handleNearMeToggle = useCallback(async () => {
    track("explore_near_me_click", {
      action: nearMeActive ? "disable" : "enable",
      had_location_cached: !!userLocation,
      view_mode: viewMode,
      radius_bucket: `${radiusKm}km`,
    }, "explore");

    if (nearMeActive) {
      exploreLocation.clear();
      return;
    }

    const ok = await exploreLocation.requestGeo();
    track("explore_near_me_result", {
      ok,
      error: ok ? undefined : "denied_or_unavailable",
      radius_bucket: `${radiusKm}km`,
    }, "explore");

    if (ok) setViewMode("map");
  }, [exploreLocation, nearMeActive, radiusKm, userLocation, viewMode]);

  const handleExpandRadius = useCallback(() => {
    setRadiusKm(25);
  }, []);

  const handleRemoveNearMe = useCallback(() => {
    exploreLocation.clear();
  }, [exploreLocation]);

  const handleRemoveParams = useCallback((updates: Partial<ExploreParams>) => {
    applyParams(updates);
  }, [applyParams]);

  const handleViewModeChange = useCallback((next: ViewMode) => {
    setViewMode(next);
    track("filter_toggle", { filter_key: `view:${next}`, enabled: true }, "explore");
  }, []);

  const handleMapItemSelect = useCallback((id: string | null) => {
    if (!id) {
      setSelectedKey(null);
      setPreviewOpen(false);
      return;
    }

    const item = results.find((result) => result.id === id);
    if (!item) return;

    if (item.kind === "activity") {
      navigate(item.href);
      return;
    }

    setSelectedKey(itemKey(item));
    setPreviewOpen(true);
  }, [navigate, results]);

  const handleExploreCardClick = useExploreCardClick();

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      favActive ||
      nearMeActive ||
      searchQuery.trim() ||
      (mode === "do" && exploreParams.when !== "upcoming") ||
      (mode === "do" && (
        exploreParams.contentTypes.length !== 2 ||
        !exploreParams.contentTypes.includes("events") ||
        !exploreParams.contentTypes.includes("outings")
      )) ||
      (mode === "places" && exploreParams.tags.length > 0) ||
      exploreParams.sort !== defaultSortForMode(mode)
    );
  }, [exploreParams, favActive, mode, nearMeActive, searchQuery]);

  const searchPlaceholder =
    mode === "do" ? "Sök utflykter och event…" : mode === "farms" ? "Sök gårdar…" : "Sök platser…";

  const modeLabel = mode === "do" ? "aktiviteter" : mode === "farms" ? "gårdar" : "platser";

  const activePathKey = deriveActivePathKey(mode, exploreParams.tags);

  return (
    <section className="py-8 md:py-12">
      <div className="container">
        {/* Hero — magnetic, editorial entry */}
        <div className="mb-8">
          <ExploreHero title={heroTitle.title} subtitle={heroTitle.subtitle} />
          {!nearMeActive && (
            <div className="mt-3">
              <ExploreLocationLink
                denied={exploreLocation.denied}
                loading={exploreLocation.loading}
                onRequestGeo={exploreLocation.requestGeo}
                onSelectCity={exploreLocation.selectCity}
              />
            </div>
          )}
        </div>

        {/* Paths — story-first discovery */}
        <div className="mb-8">
          <ExplorePaths activePathKey={activePathKey} />
        </div>

        {/* Mode toggle — secondary to paths */}
        <div className="mb-4">
          <ExploreModeToggle mode={mode} onModeChange={handleModeChange} />
        </div>

        {/* Filters bar — de-emphasized, compact */}
        <div className="mb-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex-1 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1 max-w-xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  className="pl-9"
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setFilterOpen(true)}>
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filter
                </Button>
                <Button variant="outline" size="sm" onClick={() => setSortOpen(true)}>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  {SORT_LABELS[exploreParams.sort]}
                </Button>
                <Button variant={nearMeActive ? "default" : "outline"} size="sm" onClick={handleNearMeToggle}>
                  <MapPin className="h-4 w-4 mr-2" />
                  {nearMeActive ? "Nära mig aktivt" : "Nära mig"}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-muted-foreground">{results.length} {modeLabel}</span>
              <ViewToggle viewMode={viewMode} onViewModeChange={handleViewModeChange} />
            </div>
          </div>

          <div className="mt-3">
            <ExploreActiveFilters
              mode={mode}
              params={exploreParams}
              nearMeActive={nearMeActive}
              favActive={favActive}
              onRemove={handleRemoveParams}
              onRemoveNearMe={handleRemoveNearMe}
              onRemoveFav={toggleFav}
              onClearAll={clearAllFilters}
            />
          </div>
        </div>

        {viewMode === "list" ? (
          <div>
            {isLoading && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="space-y-3">
                    <Skeleton className="aspect-[4/3] w-full rounded-xl" />
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            )}

            {!isLoading && mode === "places" && <UtflyktSection />}

            {!isLoading && results.length > 0 && (
              <div className="mt-6 space-y-8">
                {sections.filter((section) => section.items.length > 0).map((section) => {
                  const isAll = section.key === "all";
                  return (
                    <div key={section.key}>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{section.title}</h3>
                      {section.subtitle && (
                        <p className="text-sm text-muted-foreground mb-3">{section.subtitle}</p>
                      )}

                      {!isAll ? (
                        <div
                          className="flex gap-4 overflow-x-auto scrollbar-none pb-2"
                          style={{ WebkitOverflowScrolling: "touch" }}
                        >
                          {section.items.map((item, index) => (
                            <div key={itemKey(item)} className="shrink-0 w-[300px]">
                              <ExploreResultCard
                                item={mapResultToCardItem(item)}
                                isHighlighted={hoveredItemId === item.id}
                                onMouseEnter={() => setHoveredItemId(item.id)}
                                onMouseLeave={() => setHoveredItemId(null)}
                                onClick={() => handleExploreCardClick(item, section.key, index)}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {section.items.map((item, index) => (
                            <ExploreResultCard
                              key={itemKey(item)}
                              item={mapResultToCardItem(item)}
                              isHighlighted={hoveredItemId === item.id}
                              onMouseEnter={() => setHoveredItemId(item.id)}
                              onMouseLeave={() => setHoveredItemId(null)}
                              onClick={() => handleExploreCardClick(item, section.key, index)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="text-center text-sm text-muted-foreground">Visar {results.length}</div>
              </div>
            )}

            {!isLoading && mode !== "do" && results.length === 0 && (
              <EmptyState
                onClearFilters={clearAllFilters}
                hasRegionOnly={false}
                nearMeActive={nearMeActive}
                currentRadius={radiusKm}
                onExpandRadius={handleExpandRadius}
                onDisableNearMe={handleRemoveNearMe}
              />
            )}

            {!isLoading && mode === "do" && results.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                  <CalendarX className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground mb-2">Det verkar lugnt här just nu.</h3>
                <p className="text-muted-foreground mb-4 max-w-sm">
                  {hasActiveFilters
                    ? "Rensa filter eller välj en annan plats."
                    : "Kom tillbaka snart — nya upplevelser dyker upp hela tiden."}
                </p>
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearAllFilters}>Rensa filter</Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="h-[60vh] md:h-[70vh] relative" data-testid="explore-map-region">
            <ExploreMap
              items={mapItems}
              userLocation={userLocation}
              selectedItemId={selectedItem?.id ?? null}
              highlightedItemId={hoveredItemId}
              previewOpen={previewOpen}
              onItemSelect={handleMapItemSelect}
              onItemHover={setHoveredItemId}
            />

            {/* Floating rail — same pill model as mobile */}
            <div
              className="absolute left-0 right-0 bottom-3 z-20 pointer-events-none"
            >
              <div className="pointer-events-auto px-3">
                <ExploreRail
                  results={results}
                  selectedKey={selectedKey}
                  onSelectKey={(key) => {
                    setSelectedKey(key);
                  }}
                  onOpenDetails={(key) => {
                    const item = results.find((r) => itemKey(r) === key);
                    if (!item) return;

                    if (item.kind === "activity" && item.href) {
                      navigate(item.href);
                      return;
                    }

                    setSelectedKey(key);
                    setPreviewOpen(true);
                  }}
                />
              </div>
            </div>

            {!isLoading && mapItems.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
                <div className="text-center p-6 max-w-sm">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <MapPin className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-foreground/80 font-medium">Inga resultat med position hittades</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Prova att ändra dina filter eller sök på ett annat område.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        <ExplorePreviewSheet
          item={selectedItem}
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          mode={mode}
        />

        <ExploreFilterSheet
          open={filterOpen}
          onOpenChange={setFilterOpen}
          mode={mode}
          params={exploreParams}
          favActive={favActive}
          onToggleFav={toggleFav}
          onApply={applyParams}
          locationActive={nearMeActive}
          locationLabel={exploreLocation.label}
          onRequestGeo={exploreLocation.requestGeo}
          onSelectCity={exploreLocation.selectCity}
          onClearLocation={exploreLocation.clear}
          resultCount={results.length}
          onClearAll={clearAllFilters}
        />

        <ExploreSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          mode={mode}
          currentSort={exploreParams.sort}
          hasGeo={!!userLocation}
          onSelect={(sort) => applyParams({ sort })}
        />
      </div>
    </section>
  );
};

export default ExploreContent;
